import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RecommendationApprovalPanel = ({ recommendation, userRole, onApprove, onReject, onSendToClient }) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!recommendation) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Icon name="CheckCircle" size={48} color="var(--color-muted-foreground)" />
        <p className="text-sm text-muted-foreground mt-4">
          اختر توصية لعرض خيارات الموافقة
        </p>
      </div>
    );
  }

  const canApprove = () => {
    if (userRole === 'admin') return true;
    if (userRole === 'contract_specialist') return true;
    return false;
  };

  const handleApprove = async (sendToClient = false) => {
    setIsSubmitting(true);
    await onApprove(recommendation?.id, sendToClient);
    setComment('');
    setIsSubmitting(false);
  };

  const handleReject = async () => {
    if (!comment?.trim()) {
      alert('الرجاء إضافة سبب الرفض');
      return;
    }
    setIsSubmitting(true);
    await onReject(recommendation?.id, comment);
    setComment('');
    setIsSubmitting(false);
  };

  const handleSendToClient = async () => {
    setIsSubmitting(true);
    await onSendToClient(recommendation?.id);
    setIsSubmitting(false);
  };

  const isPending = recommendation?.approvalStatus === 'pending';
  const isApproved = recommendation?.approvalStatus === 'approved';
  const isRejected = recommendation?.approvalStatus === 'rejected';
  const isSent = recommendation?.sentToClient;

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 md:p-6 border-b border-border">
        <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-2">
          إجراءات الموافقة
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground">
          الحالة: {isApproved ? 'تمت الموافقة' : isRejected ? 'مرفوض' : 'قيد المراجعة'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="space-y-6">
          {/* Status Info */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {isApproved ? (
                  <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  </div>
                ) : isRejected ? (
                  <div className="w-8 h-8 rounded-full bg-error/20 flex items-center justify-center">
                    <Icon name="XCircle" size={20} color="var(--color-error)" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-warning/20 flex items-center justify-center animate-pulse">
                    <Icon name="Clock" size={20} color="var(--color-warning)" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground mb-1">
                  {isApproved ? 'تمت الموافقة على التوصية' : isRejected ? 'تم رفض التوصية' : 'في انتظار الموافقة'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isApproved
                    ? 'يمكنك الآن إرسال التوصية للعميل'
                    : isRejected
                    ? 'تم رفض هذه التوصية ولن يتم إرسالها للعميل' :'يرجى مراجعة التوصية واتخاذ القرار المناسب'}
                </p>
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div>
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="User" size={18} />
              معلومات العميل
            </h4>
            <div className="bg-card border border-border rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">الاسم:</span>
                <span className="text-sm text-foreground font-medium">{recommendation?.clientName}</span>
              </div>
              {recommendation?.clientEmail && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">البريد الإلكتروني:</span>
                  <span className="text-sm text-foreground font-data">{recommendation?.clientEmail}</span>
                </div>
              )}
              {recommendation?.clientPhone && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">الهاتف:</span>
                  <span className="text-sm text-foreground font-data">{recommendation?.clientPhone}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">الفئة:</span>
                <span className="text-sm text-foreground">{recommendation?.clientCategory}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          {canApprove() && (
            <div>
              <h4 className="text-sm md:text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Settings" size={18} />
                الإجراءات المتاحة
              </h4>
              <div className="space-y-3">
                {isPending && (
                  <>
                    <Button
                      onClick={() => handleApprove(false)}
                      disabled={isSubmitting}
                      className="w-full bg-success hover:bg-success/90 text-white"
                      aria-label="الموافقة على التوصية"
                    >
                      <Icon name="CheckCircle" size={18} />
                      <span>{isSubmitting ? 'جاري المعالجة...' : 'الموافقة على التوصية'}</span>
                    </Button>
                    <Button
                      onClick={() => handleApprove(true)}
                      disabled={isSubmitting}
                      className="w-full bg-primary hover:bg-primary/90 text-white"
                      aria-label="الموافقة والإرسال للعميل"
                    >
                      <Icon name="Send" size={18} />
                      <span>{isSubmitting ? 'جاري المعالجة...' : 'الموافقة والإرسال للعميل'}</span>
                    </Button>
                    <div className="space-y-2">
                      <Input
                        type="text"
                        placeholder="سبب الرفض (مطلوب)"
                        value={comment}
                        onChange={(e) => setComment(e?.target?.value)}
                        className="w-full"
                        aria-label="سبب الرفض"
                      />
                      <Button
                        onClick={handleReject}
                        disabled={isSubmitting || !comment?.trim()}
                        className="w-full bg-error hover:bg-error/90 text-white"
                        aria-label="رفض التوصية"
                      >
                        <Icon name="XCircle" size={18} />
                        <span>{isSubmitting ? 'جاري المعالجة...' : 'رفض التوصية'}</span>
                      </Button>
                    </div>
                  </>
                )}
                {isApproved && !isSent && (
                  <Button
                    onClick={handleSendToClient}
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-white"
                    aria-label="إرسال للعميل"
                  >
                    <Icon name="Send" size={18} />
                    <span>{isSubmitting ? 'جاري الإرسال...' : 'إرسال للعميل'}</span>
                  </Button>
                )}
                {isSent && (
                  <div className="bg-success/10 border border-success/30 rounded-lg p-4 text-center">
                    <Icon name="CheckCircle" size={24} color="var(--color-success)" className="mx-auto mb-2" />
                    <p className="text-sm text-success font-medium">تم إرسال التوصية للعميل بنجاح</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rejection Info */}
          {isRejected && recommendation?.rejectionReason && (
            <div className="bg-error/10 border border-error/30 rounded-lg p-4">
              <p className="text-xs font-semibold text-error mb-2 flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                سبب الرفض:
              </p>
              <p className="text-sm text-foreground">{recommendation?.rejectionReason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationApprovalPanel;